/**
 * Step2Contact — second step of the unified <LeadForm>.
 *
 * Collects name + phone (always), email (when the variant includes it).
 * CTA layout flips with `variant.leadForm.primaryChannel`:
 *   - 'whatsapp' → green WA pill on top, "Enviar por correo" link below
 *   - 'email'    → blue submit pill on top, optional WA link below
 *
 * The form's onSubmit calls `submit({ channel: 'email' })`. The WA button
 * is `type="button"` and calls `submit({ channel: 'whatsapp' })` —
 * critical: a non-submit button inside a <form> defaults to "submit",
 * see CLAUDE.md "Buttons Inside Forms".
 */

import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import { isValidPhoneNumber as isValidNumber } from 'libphonenumber-js/min'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useForm } from 'react-hook-form'

import BaseTextField from 'src/components/Inputs/BaseTextField'
import PhoneField from 'src/components/Inputs/PhoneField'
import type {
  LeadCopyKey,
  LeadFormChannel,
  LeadFormField
} from 'src/lib/listingVariants/types'
import { RedirectUrls } from 'src/types/types'
import WhatsappIcon from '../../../../public/svgs/whatsapp-icon.svg'

import type {
  LeadFormError,
  LeadFormValues
} from 'src/components/LeadForm/useLeadFormState'

/**
 * Step-2 header subtitle, selected by the variant's `leadCopyKey` — the SAME
 * discriminant Step 3 reads, deliberately not a second field (see
 * `LeadCopyKey`).
 *
 * 🔴 The `traditional` line carries NO response-time promise and names NO
 * counterparty, and both halves are load-bearing. This header renders on the
 * mobile bottom sheet of every Traditional listing (`specialistKey: null` +
 * `leadType: 'demand'`), which is where most MX traffic submits from. The
 * default line — "te respondemos por WhatsApp en menos de 2 horas" — is a
 * SpotMe-voice SLA, and on a partner-owned Traditional listing SpotMe does not
 * respond at all: the operator does, on no timetable we control. On the nudge
 * tier the operator never even receives the renter's details and a SpotMe
 * asesor works the lead instead. An unnamed party with no deadline is the only
 * wording true of both, which is why it mirrors `BODY_TRADITIONAL`'s
 * discipline one screen later. Do not "improve" it by naming the operator, and
 * do not restore a time window here on any variant that is not handled by a
 * SpotMe asesor.
 *
 * Literal keys, not a template string: the typed-`t()` contract forbids
 * building keys dynamically.
 */
const STEP2_HEADER_SUBTITLE = {
  default: 'LEAD_FORM.STEP2.HEADER_SUBTITLE',
  traditional: 'LEAD_FORM.STEP2.HEADER_SUBTITLE_TRADITIONAL'
} as const

type ContactFormShape = {
  name: string
  phone: string
  email: string
}

type Step2ContactProps = {
  values: LeadFormValues
  setField: <K extends keyof LeadFormValues>(
    key: K,
    value: LeadFormValues[K]
  ) => void
  fields: ReadonlyArray<LeadFormField>
  primaryChannel: LeadFormChannel
  /**
   * When true, swap the neutral "Continuar por WhatsApp" CTA for a
   * booking-framed "Reservar por WhatsApp" + microcopy line that signals
   * the request → confirmation step. Driven by
   * `LeadFormConfig.useBookingFraming`; true on Traditional only.
   */
  useBookingFraming?: boolean
  isSubmitting: boolean
  error: LeadFormError | null
  /**
   * `overrides` lets us thread the fresh form values past React's
   * `setField` batching window — the previous render's `state.values`
   * is captured inside the hook's `submit`, so without overrides the
   * WA prefill landed empty for name/phone/email.
   */
  onSubmit: (
    channel: LeadFormChannel,
    overrides?: Partial<LeadFormValues>
  ) => void
  onBack?: () => void
  /**
   * Render the editorial step header (eyebrow + title + subtitle) above
   * the form fields. Used when nothing else (e.g. SpecialistCard) is
   * sitting above Step 2 to provide framing — without it, the step jumps
   * straight to three input rows and reads like a generic web form.
   *
   * Today this fires for Traditional listings only (their LeadForm has
   * `specialistKey: null`, so SpecialistCard never mounts inside the
   * drawer). Broker + Fleet keep the SpecialistCard above and don't need
   * a second header.
   */
  showStepHeader?: boolean
  /**
   * Which lead-copy set to render — passed straight through from
   * `LeadFormConfig.leadCopyKey`. Required, with no default: a mount that
   * forgot to state it would silently inherit the SpotMe-asesor promises,
   * which are false on an off-platform lead.
   */
  leadCopyKey: LeadCopyKey
  /**
   * Compact mode — tightens the vertical rhythm and drops the lock ICON from
   * the privacy footnote. Used by the desktop always-expanded inline-on-card
   * mount inside the listing PriceCard, where the surrounding page already
   * provides plenty of trust framing (price block above, trust pills below)
   * and the right-rail height is a binding constraint. Defaults to false so
   * mobile sheets and page-section mounts keep the full editorial rhythm.
   *
   * 🔴 It does NOT drop the privacy footnote itself, and must never be made
   * to — that line is the only disclosure of the PII transfer in this funnel.
   * See the note on the footnote below.
   */
  compact?: boolean
  /** Drop the email-path "Hablar con asesor" WhatsApp button
   *  (`LeadFormConfig.hideWhatsappFallback`). */
  hideWhatsappFallback?: boolean
}

export default function Step2Contact({
  values,
  setField,
  fields,
  primaryChannel,
  useBookingFraming = false,
  isSubmitting,
  error,
  onSubmit,
  onBack,
  showStepHeader = false,
  leadCopyKey,
  compact = false,
  hideWhatsappFallback = false
}: Step2ContactProps) {
  const { t } = useTranslation('details')

  const emailField = fields.find((field) => field.name === 'email')
  const includeEmail = !!emailField
  // The variant's `required` flag is the source of truth. Listing-level
  // variants (Broker / Traditional / Fleet pill) ship email required;
  // the /flotillas hero overrides to `required: false` (2026-06-11
  // friction cut — fleet sales follows up via WhatsApp/phone, and the
  // form's 92% abandonment clustered on the email row). When optional,
  // the format validation still runs on any non-empty value.

  // RHF gives us validation + PhoneField integration without rebuilding
  // either. We mirror writes back into the parent state machine so the
  // hook's `values` stay authoritative.
  const { control, handleSubmit, formState } = useForm<ContactFormShape>({
    defaultValues: {
      name: values.name,
      phone: values.phone,
      email: values.email
    }
  })

  const submitViaEmail = handleSubmit((formValues) => {
    // Mirror values into the parent state machine for downstream consumers
    // (e.g. the success step re-reads `state.values`).
    setField('name', formValues.name.trim())
    setField('phone', formValues.phone.trim())
    if (includeEmail) {
      setField('email', formValues.email.trim())
    }
    // Pass fresh values as overrides so the submit doesn't depend on the
    // batched dispatch landing before `submit({channel})` reads state.
    onSubmit('email', {
      name: formValues.name.trim(),
      phone: formValues.phone.trim(),
      ...(includeEmail ? { email: formValues.email.trim() } : {})
    })
  })

  const submitViaWhatsapp = handleSubmit((formValues) => {
    setField('name', formValues.name.trim())
    setField('phone', formValues.phone.trim())
    if (includeEmail) {
      setField('email', formValues.email.trim())
    }
    // Same trick — without these overrides, the WA prefill arrived with
    // empty name/phone/email because of React state batching.
    onSubmit('whatsapp', {
      name: formValues.name.trim(),
      phone: formValues.phone.trim(),
      ...(includeEmail ? { email: formValues.email.trim() } : {})
    })
  })

  return (
    <form
      className={`flex flex-col ${compact ? 'gap-3' : 'gap-5'}`}
      onSubmit={submitViaEmail}
      noValidate
    >
      {showStepHeader && (
        <div className="flex flex-col gap-2">
          <p className="font-display text-[10px] tracking-[0.22em] text-blue-500 uppercase">
            {t('LEAD_FORM.STEP2.HEADER_EYEBROW')}
          </p>
          <h3 className="font-display text-[20px] leading-tight font-medium text-ink-700 md:text-[22px]">
            {t('LEAD_FORM.STEP2.HEADER_TITLE')}
          </h3>
          <p className="text-[13px] leading-relaxed text-ink-200">
            {t(STEP2_HEADER_SUBTITLE[leadCopyKey])}
          </p>
        </div>
      )}

      {/* Field affordance: per the project's design rule (≤4 fields →
          placeholder-only, default size), we drop `topLabel` in
          `compact` mode and rely on the placeholders alone. The label
          row in `FieldLabel` is ~28px each; with 3 fields stacked
          that's ~84px the desktop inline-on-card form can't afford on
          a 13" MacBook Pro viewport. Mobile sheets + page-section
          mounts keep the labels for clarity. */}
      <BaseTextField
        id="name"
        control={control}
        rules={{
          required: t('LEAD_FORM.STEP2.NAME_REQUIRED'),
          minLength: { value: 2, message: t('LEAD_FORM.STEP2.NAME_REQUIRED') }
        }}
        topLabel={compact ? undefined : t('LEAD_FORM.STEP2.NAME_LABEL')}
        placeholder={t('LEAD_FORM.STEP2.NAME_PLACEHOLDER')}
        fullWidth
        error={!!formState.errors.name}
        slotProps={{
          htmlInput: { className: 'text-base md:text-sm' }
        }}
      />

      <PhoneField<ContactFormShape>
        name="phone"
        control={control}
        topLabel={compact ? undefined : t('LEAD_FORM.STEP2.PHONE_LABEL')}
        placeholder={t('LEAD_FORM.STEP2.PHONE_PLACEHOLDER')}
        required
        fullWidth
        rules={{
          required: t('LEAD_FORM.STEP2.PHONE_REQUIRED'),
          validate: (value: string) =>
            isValidNumber(value || '') || t('LEAD_FORM.STEP2.PHONE_INVALID')
        }}
      />

      {includeEmail && (
        <BaseTextField
          id="email"
          control={control}
          rules={{
            required: emailField.required
              ? t('LEAD_FORM.STEP2.EMAIL_REQUIRED')
              : false,
            validate: (value: string) =>
              (!emailField.required && !value.trim()) ||
              /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) ||
              t('LEAD_FORM.STEP2.EMAIL_INVALID')
          }}
          type="email"
          topLabel={
            compact
              ? undefined
              : emailField.required
                ? t('LEAD_FORM.STEP2.EMAIL_LABEL')
                : t('LEAD_FORM.STEP2.EMAIL_OPTIONAL_LABEL')
          }
          placeholder={
            emailField.required
              ? t('LEAD_FORM.STEP2.EMAIL_PLACEHOLDER')
              : t('LEAD_FORM.STEP2.EMAIL_PLACEHOLDER_OPTIONAL')
          }
          fullWidth
          error={!!formState.errors.email}
          slotProps={{
            htmlInput: { className: 'text-base md:text-sm' }
          }}
        />
      )}

      {error && (
        <p className="text-[13px] leading-relaxed text-red-500">
          {error.message}
        </p>
      )}

      {/* WA-pill validation hint. The green WA pill calls `handleSubmit`
          under the hood, so RHF validation runs identically for both
          channels — but renters historically clicked WA expecting an
          instant hand-off and nothing visible happened when name/phone/
          email were incomplete (the inline field errors did fire, but
          weren't connected back to the WA pill in the renter's head).
          This sub-line ties them together: when validation has surfaced
          errors and the renter has tried submitting, we point them at
          the fields they need to finish. Fires whether the renter
          clicked WA or the email link — the hint applies to both. */}
      {primaryChannel === 'whatsapp' &&
        Object.keys(formState.errors).length > 0 && (
          <p className="text-[12px] leading-snug text-ink-200">
            {t('LEAD_FORM.STEP2.WA_REQUIRED_HINT')}
          </p>
        )}

      {/* Privacy notice — sits at the friction moment (just after the
          renter has typed their phone + email, just before submission).
          Compact muted footnote: lock icon + single short line, no box
          chrome. The legacy cream box + blue left rule + 2-3-line copy
          was eating ~60-80px of vertical mass on the narrow Flotillas
          hero card; this is the same signal at ~20px.

          🔴 This is a LEGAL NOTICE, not decoration, and it is the ONLY
          point in this funnel where the renter is told their contact
          details leave SpotMe. The LeadForm feeds
          `NotificationService.dispatchHostLead`, which forwards name +
          phone + email + message to partner teams on the FULL
          disclosure tier (`Team.isPartner`, 19 teams as of 2026-08-26).
          Aviso de Privacidad §V permits that transfer; this line is the
          disclosure that makes it honest, so:
            - NEVER restate a "no los compartimos" / "we never share it"
              promise here. That is exactly what this replaced
              (2026-08-28) and it was false on both halves — it also
              carried an unreviewed "anfitrión verificado" verification
              claim. See root CLAUDE.md → "Code ↔ Terms ↔ Marketing ↔
              Help Center" and "Off-limits without legal review".
            - NEVER gate it behind `compact`. It previously rendered
              only in the tall layout; the notice has to appear wherever
              the contact fields do. In `compact` we drop the lock icon
              instead, which costs no vertical rows.
            - NEVER make it per-variant. The header subtitle above and
              Step 3 next door ARE per-variant (`leadCopyKey`), and the
              asymmetry is deliberate, not an
              oversight: Step 3 describes what happens on THIS listing,
              while this line is the consent disclosure — including the
              "…y preguntarte después cómo te fue" follow-up clause
              (spec §A3) — and it has to be shown to every renter on
              every variant and every `leadType: 'supply'` page before
              they submit, whether or not their particular lead ends up
              enrolling in the verification ladder.
          The link opens in a new tab on purpose — navigating away
          mid-form would discard the renter's typed values. */}
      <p className="flex items-start gap-1.5 text-[11.5px] leading-snug text-ink-200 md:text-[12px]">
        {!compact && (
          <LockOutlinedIcon
            sx={{ fontSize: 12 }}
            className="mt-[3px] shrink-0 text-blue-500"
          />
        )}
        <span>
          {`${t('LEAD_FORM.STEP2.PRIVACY')} `}
          <Link
            href={RedirectUrls.Disclosures}
            target="_blank"
            rel="noopener noreferrer"
            className="underline underline-offset-2 transition-colors hover:text-ink-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink-700"
          >
            {t('LEAD_FORM.STEP2.PRIVACY_LINK')}
          </Link>
        </span>
      </p>

      {/*
        Unified CTA stack: filled primary pill + plain text-link secondary.
        Mirrors the editorial rhythm — the primary owns the visual weight,
        the secondary is a quiet escape hatch. With Fleet now also WA-primary
        the email-primary branch is unreachable in production, but we keep
        it functional + visually consistent (text-link secondary) so a future
        email-primary variant inherits the same shape.
      */}
      {primaryChannel === 'whatsapp' ? (
        <>
          <button
            type="button"
            disabled={isSubmitting}
            onClick={submitViaWhatsapp}
            className="mt-2 inline-flex w-full items-center justify-center gap-2 rounded-full bg-green-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(34,197,94,0.55)] transition-all duration-200 hover:bg-green-600 active:scale-[0.98] disabled:opacity-60"
          >
            <Image
              src={WhatsappIcon}
              alt=""
              width={16}
              height={16}
              aria-hidden="true"
            />
            {isSubmitting
              ? t('LEAD_FORM.STEP2.SUBMITTING')
              : useBookingFraming
                ? t('LEAD_FORM.STEP2.WHATSAPP_CTA_BOOKING')
                : t('LEAD_FORM.STEP2.WHATSAPP_CTA')}
          </button>
          {useBookingFraming && (
            <p className="-mt-1 text-center text-[11px] leading-relaxed text-ink-200 md:text-xs">
              {t('LEAD_FORM.STEP2.WHATSAPP_MICROCOPY_BOOKING')}
            </p>
          )}
          <button
            type="submit"
            disabled={isSubmitting}
            className="text-[13px] font-medium text-blue-500 underline-offset-4 hover:underline disabled:opacity-60"
          >
            {t('LEAD_FORM.STEP2.EMAIL_LINK')}
          </button>
        </>
      ) : (
        <>
          <button
            type="submit"
            disabled={isSubmitting}
            className="mt-2 w-full rounded-full bg-blue-500 px-7 py-3.5 text-center text-[15px] font-semibold text-white-500 shadow-[0_20px_40px_-16px_rgba(0,164,255,0.55)] transition-all duration-200 hover:bg-blue-600 active:scale-[0.98] disabled:opacity-60"
          >
            {isSubmitting
              ? t('LEAD_FORM.STEP2.SUBMITTING')
              : t('LEAD_FORM.STEP2.EMAIL_CTA')}
          </button>
          {!hideWhatsappFallback && (
            <button
              type="button"
              disabled={isSubmitting}
              onClick={submitViaWhatsapp}
              className="inline-flex items-center justify-center gap-1.5 text-[13px] font-medium text-green-500 underline-offset-4 hover:underline disabled:opacity-60"
            >
              <Image
                src={WhatsappIcon}
                alt=""
                width={14}
                height={14}
                aria-hidden="true"
              />
              {t('LEAD_FORM.STEP2.WHATSAPP_LINK')}
            </button>
          )}
        </>
      )}

      {onBack && (
        <button
          type="button"
          onClick={onBack}
          disabled={isSubmitting}
          className="-mt-2 self-center text-[12px] text-ink-200 underline-offset-4 hover:underline disabled:opacity-60"
        >
          {t('LEAD_FORM.STEP2.BACK')}
        </button>
      )}
    </form>
  )
}
